import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { client } from '../lib/sanityClient';
import { PortableText } from '@portabletext/react';
import '../styles/documentation.css';

interface Event {
  _id: string;
  title: string;
  eventType: string;
  description: any; // Portable Text format
  startDate: string;
  endDate: string;
  openingReception?: {
    date: string;
    timeStart: string;
    timeEnd: string;
    timezone: string;
  };
  closingReception?: {
    date: string;
    timeStart: string;
    timeEnd: string;
    timezone: string;
  };
  galleryHours?: string;
  venue: string;
  venueAddress: string;
  bannerImageUrl: string;
  externalLink?: string;
  documentationImages?: Array<{
    url: string;
    alt?: string;
  }>;
  documentationDescription?: any; // Portable Text format
  attachment?: {
    url: string;
    originalFilename: string;
  };
  participants: Array<{
    name: string;
    role: string;
    profileImageUrl?: string;
    slug?: string;
  }>;
  featuredArtworks: Array<{
    _id: string;
    title: string;
    slug?: string;
    imageUrl: string;
  }>;
  slug?: string;
}

// Image tile component with expandable functionality
const ImageModal: React.FC<{
  images: Array<{ url: string; alt?: string }>;
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}> = ({ images, currentIndex, onClose, onNext, onPrevious }) => {
  const currentImage = images[currentIndex];
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') onNext();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') onPrevious();
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrevious, onClose]);
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-screen-xl max-h-screen w-full">
        {/* Close button */}
        <button 
          className="absolute top-4 right-4 text-white text-3xl z-20"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>
        
        {/* Previous button */}
        {images.length > 1 && (
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl opacity-70 hover:opacity-100 z-20"
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
          >
            ‹
          </button>
        )}
        
        {/* Next button */}
        {images.length > 1 && (
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl opacity-70 hover:opacity-100 z-20"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            ›
          </button>
        )}
        
        {/* Current image */}
        <img
          src={currentImage.url}
          alt={currentImage.alt || `Documentation image ${currentIndex + 1}`}
          className="max-h-screen max-w-full object-contain mx-auto z-10"
        />
        
        {/* Image counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full z-20">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

const ImageTile: React.FC<{
  image: { url: string; alt?: string };
  index: number;
  allImages: Array<{ url: string; alt?: string }>;
}> = ({ image, index, allImages }) => {
  const [expanded, setExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(index);
  
  // Navigation handlers
  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
    );
  };
  
  const handleClose = () => setExpanded(false);
  
  return (
    <>
      <div 
        className="aspect-[4/3] overflow-hidden cursor-pointer w-full"
        onClick={() => {
          setCurrentIndex(index); // Ensure we start at the clicked image
          setExpanded(true);
        }}
      >
        <img
          src={image.url}
          alt={image.alt || `Documentation image ${index + 1}`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Full-screen image modal */}
      {expanded && (
        <ImageModal
          images={allImages}
          currentIndex={currentIndex}
          onClose={handleClose}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      )}
    </>
  );
};

const SingleEventPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Fetch detailed event data
  const { data: event, error, isLoading } = useQuery({
    queryKey: ['event', slug],
    queryFn: async () => {
      try {
        const data = await client.fetch<Event>(
          `*[_type == "event" && (slug.current == $slug || _id == $slug)][0]{
            _id, 
            title, 
            eventType,
            description,
            startDate, 
            endDate,
            openingReception,
            closingReception,
            galleryHours,
            venue,
            venueAddress,
            externalLink,
            "bannerImageUrl": bannerImage.asset->url,
            "documentationImages": documentationImages[]{
              "url": asset->url,
              "alt": _key
            },
            documentationDescription,
            "attachment": {
              "url": attachment.asset->url,
              "originalFilename": attachment.asset->originalFilename
            },
            "slug": slug.current,
            "participants": participants[]->{ 
              "name": name,
              "role": "Artist",
              "profileImageUrl": profileImage.asset->url,
              "slug": slug.current,
              "_id": _id
            },
            "featuredArtworks": featuredArtworks[]->{ 
              _id,
              title,
              "slug": slug.current,
              "imageUrl": image.asset->url
            }
          }`,
          { slug }
        );
        return data;
      } catch (err) {
        console.error('Error fetching event:', err);
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return <div className="w-full h-screen flex items-center justify-center">Loading event details...</div>;
  }

  if (error) {
    return <div className="w-full h-screen flex items-center justify-center text-red-600">Error loading event: {error.message}</div>;
  }

  if (!event) {
    return <div className="w-full h-screen flex items-center justify-center">Event not found</div>;
  }

  return (
    <div className="min-h-screen bg-white pt-[60px]">
      {/* Main Content Container */}
      <div className="w-full max-w-[2000px] mx-auto px-6 pt-[10px]">
        {/* Top section with banner image and details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full mb-16">
          {/* Image container - vertical format */}
          {event.bannerImageUrl && (
            <div className="aspect-[2/3] mb-8 md:col-span-1 w-full">
              <img
                src={event.bannerImageUrl}
                alt={event.title || ''}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Text content */}
          <div className="space-y-6 md:col-span-2">
            {/* Title at the top of the text content */}
            <h2 className="text-4xl font-light mb-6">{event.title}</h2>
            <div className="space-y-4 text-lg">
              {event.startDate && (
                <div className="flex flex-col gap-1">
                  <div className="text-lg font-medium">
                    {event.eventType}
                    {event.endDate ? (
                      <span className="font-normal text-base ml-2">
                        {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="font-normal text-base ml-2">
                        {new Date(event.startDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Opening Reception Details */}
              {event.openingReception?.date && (
                <div className="flex flex-col gap-1">
                  <div className="font-medium">OPENING:</div>
                  <div>
                    {new Date(event.openingReception.date).toLocaleDateString()}, {event.openingReception.timeStart} - {event.openingReception.timeEnd} {event.openingReception.timezone}
                  </div>
                </div>
              )}
              
              {/* Closing Reception Details */}
              {event.closingReception?.date && (
                <div className="flex flex-col gap-1">
                  <div className="font-medium">CLOSING:</div>
                  <div>
                    {new Date(event.closingReception.date).toLocaleDateString()}, {event.closingReception.timeStart} - {event.closingReception.timeEnd} {event.closingReception.timezone}
                  </div>
                </div>
              )}
              
              {/* Location and Hours */}
              {(event.venue || event.venueAddress || event.galleryHours) && (
                <div className="mt-4">
                  <div className="flex flex-col space-y-1">
                    {event.venue && (
                      <div>
                        <span className="font-medium">VENUE: </span>
                        <span>{event.venue}</span>
                        {event.venueAddress && <div>{event.venueAddress}</div>}
                      </div>
                    )}
                    {event.galleryHours && (
                      <div>
                        <span className="font-medium">OPEN: </span>
                        <span>{event.galleryHours}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* External Link as Partner Website */}
              {event.externalLink && (
                <div className="mt-4">
                  <div className="flex flex-col gap-6 w-full">
                    <div className="w-full space-y-6">
                      <a 
                        href={event.externalLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium hover:text-gray-600 transition-colors"
                      >
                        PARTNER WEBSITE ›
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Description */}
            {event.description && (
              <div className="text-lg leading-relaxed text-justify mb-2">
                <PortableText value={event.description} />
              </div>
            )}
            {/* Attachment Download Button - subtle, minimalist, under description */}
            {event.attachment && event.attachment.url && (
              <div className="flex items-center mt-2 mb-2">
                <a
                  href={event.attachment.url}
                  download={event.attachment.originalFilename || 'attachment'}
                  className="flex items-center gap-2 px-3 py-2 border border-black/20 rounded-md text-sm text-black hover:bg-black/5 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-black/10 opacity-70 hover:opacity-90"
                  style={{ textDecoration: 'none', width: 'fit-content', maxWidth: '100%' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.2} stroke="currentColor" className="w-4 h-4 opacity-60">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 3.5v7.25m0 0l3-3m-3 3l-3-3M18 16.5a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 012 16.5V14.5a1.5 1.5 0 011.5-1.5h13a1.5 1.5 0 011.5 1.5v2z" />
                  </svg>
                  <span className="truncate" style={{maxWidth: 220, display: 'inline-block'}} title={event.attachment.originalFilename}>{event.attachment.originalFilename}</span>
                </a>
              </div>
            )}
          </div>
        </div>
        
        {/* Participants Section */}
        {event.participants?.length > 0 && (
          <div className="mt-12 pt-6 border-t border-gray-200 w-full">
            <h3 className="text-xl font-medium mb-4">Participants</h3>
            <div className="space-y-2">
              {event.participants.map(participant => (
                <div key={participant.name} className="flex items-center gap-2">
                  {participant.slug ? (
                    <Link 
                      to={`/artist/${participant.slug}`} 
                      className="font-medium hover:text-gray-600 transition-colors"
                    >
                      {participant.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{participant.name}</span>
                  )}
                  <span className="text-gray-600">({participant.role})</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Featured Artworks Section */}
        {event.featuredArtworks?.length > 0 && (
          <div className="mt-12 pt-6 border-t border-gray-200 w-full">
            <h3 className="text-xl font-medium mb-4">Featured Artworks</h3>
            <div className="flex flex-wrap gap-4">
              {event.featuredArtworks.map(artwork => (
                <Link 
                  key={artwork._id} 
                  to={`/works/${artwork.slug || ''}`}
                  className="group relative block"
                >
                  <div className="w-16 h-16 overflow-hidden rounded-md">
                    <img 
                      src={artwork.imageUrl} 
                      alt={artwork.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-1 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-xs truncate">{artwork.title}</p>
                  </div>
                </Link>
              ))}
            </div>

          </div>
        )}
      </div>
      
      {/* Documentation Section - OUTSIDE main container for full width */}
      {((event.documentationImages && event.documentationImages.length > 0) || event.documentationDescription) && (
        <section className="mt-16">
          <div className="w-full border-t border-gray-200"></div>
          <div className="w-full max-w-[2000px] mx-auto px-6 pt-8">
            <h3 className="text-xl font-medium mb-4">Documentation</h3>
            {event.documentationImages && event.documentationImages.length > 0 && (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 w-full">
    {event.documentationImages.map((image, index) => (
      <ImageTile 
        key={index} 
        image={image} 
        index={index} 
        allImages={event.documentationImages || []} 
      />
    ))}
  </div>
)}
          </div>
          {event.documentationDescription && (
  <div
    style={{
      width: '100vw',
      maxWidth: '100vw',
      position: 'relative',
      left: '50%',
      right: '50%',
      marginLeft: '-50vw',
      marginRight: '-50vw',
      padding: '0',
      backgroundColor: 'white',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        width: '100%',
        padding: '20px',
        boxSizing: 'border-box',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          width: '100%',
          textAlign: 'justify',
          fontSize: '1.125rem',
          lineHeight: '1.75rem',
        }}
      >
        <PortableText value={event.documentationDescription} />
      </div>
    </div>
  </div>
)}
        </section>
      )}
    {/* Elegant Attachment Download Section */}

    </div>
  );
};

export default SingleEventPage;
